// @flow

import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type IncomingPayment, getAmountUsd, getMerchant } from '@tectonics/prs/incoming-payment';
import { getPublicIntegrationKey, getName as getMerchantName } from '@tectonics/prs/merchant';
import { type OutgoingPayment } from '@tectonics/prs/outgoing-payment';
import { getGooglePayClientInstance } from './get-client';
import { GooglePayError, GooglePayCanceledError } from './google-pay-error';

export async function googlePayOutgoingPaymentMapper(
  userInputValues: UserInputValues,
  incomingPayment: IncomingPayment,
): Promise<$Shape<OutgoingPayment>> {
  const googlePayClient = getGooglePayClientInstance();

  if (!googlePayClient) {
    throw new Error('Google Pay Outgoing Payment Mapper - Something went wrong with Google Pay Client');
  }

  await googlePayClient.isReadyToPay({
    allowedPaymentMethods: GOOGLE_ALLOWED_PAYMENT_METHODS,
  });

  const merchant = getMerchant(incomingPayment);

  const googlePaymentRequestData = buildGooglePayPaymentData({
    merchantPublicKey: getPublicIntegrationKey(merchant),
    merchantName: getMerchantName(merchant),
    amountUsd: getAmountUsd(incomingPayment),
  });

  try {
    const googlePaymentResponseData = await googlePayClient.loadPaymentData(googlePaymentRequestData);

    return {
      card: {
        card_name: googlePaymentResponseData.cardInfo.billingAddress.name,
        google_payments: googlePaymentResponseData.paymentMethodToken.token,
      },
      payment_type: 'google',
      payment_type_code: googlePaymentResponseData.cardInfo.cardNetwork.toLowerCase(),
      instalments: userInputValues['selectedInstalment'],
    };
  } catch(e) {
    if (e.statusCode === 'CANCELED') {
      throw new GooglePayCanceledError();
    } else {
      throw new GooglePayError(e);
    }
  }
}

const GOOGLE_ALLOWED_PAYMENT_METHODS = ['CARD', 'TOKENIZED_CARD'];

type BuildGoogleDataArgs = {|
  +merchantPublicKey: string,
  +merchantName: string,
  +amountUsd: number,
|};

function buildGooglePayPaymentData({ merchantPublicKey, merchantName, amountUsd }: BuildGoogleDataArgs): {} {
  return {
    merchantId: ENV_CONSTANTS.PWG_MERCHANT_ID,
    merchantInfo: {
      merchantName,
    },
    paymentMethodTokenizationParameters: {
      tokenizationType: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: 'ebanx',
        gatewayMerchantId: merchantPublicKey,
      },
    },
    allowedPaymentMethods: GOOGLE_ALLOWED_PAYMENT_METHODS,
    cardRequirements: {
      allowedCardNetworks: ['MASTERCARD', 'VISA'],
      billingAddressRequired: true,
    },
    transactionInfo: {
      currencyCode: 'USD',
      totalPriceStatus: 'FINAL',
      totalPrice: amountUsd,
    },
    options: {},
  };
}
